import React, { FC, useContext, useMemo } from 'react';

import { Form, useFormikContext } from 'formik';

// components
import BasicButton from '../../../../components/buttons/basicButton';
import { SearchableSelectType } from '../../../../components/inputs/searchableSelect';
import TextInput from '../../../../components/inputs/textInput';
import AampeCreateSelectDropdown from '../../../../components/shared/AampeCreateSelectDropdown/AampeCreateSelectDropdown';
import AampeTooltip from '../../../../components/shared/AampeTooltip/AampeTooltip';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../../constants/aampeTheme';

// context
import { EditorContext } from '../../../../context/EditorContext';

import { createShouldForwardProp } from 'helpers/shouldForwardProp';
import getCapitalizedText from 'helpers/stringHelpers';

import useGetSingleTestMessageUser from '../../../../hooks/contact_notification_token/useGetSingleTestMessageUser';

// models
import {
    FormulaTestMessageFormValues,
    FormulaTypesData,
    TestMessageType,
} from '../../../../models/formula.model';
import { PushProviders } from '../../../../models/pushProvider.model';

// helper
import {
    getDisableTestEmailMessage,
    getTestMessageIdFieldLabel,
} from '../../helper';

interface TestFormulaOneUserFormProps {
    isLoading: boolean;
    menuPortalId?: string;
    handleClose?: () => void;
    isModalView?: boolean;
    providerId?: string;
    providerName?: PushProviders;
    handleNeedHelp?: () => void;
}
const shouldForwardProps = createShouldForwardProp(['isModalView']);
const TestFormulaOneUserForm: FC<
    React.PropsWithChildren<TestFormulaOneUserFormProps>
> = ({
    isLoading,
    menuPortalId,
    isModalView = false,
    providerId,
    providerName,
    handleNeedHelp,
}) => {
    const { values, errors, touched, handleBlur, setFieldValue } =
        useFormikContext<FormulaTestMessageFormValues>();
    const {
        editorDetails: { formulaType, formula },
    } = useContext(EditorContext);

    const testMessageInfo =
        formulaType && FormulaTypesData[formulaType].testMessageInfo;

    const { data: users } = useGetSingleTestMessageUser({
        push_provider_id: providerId,
    });

    const notificationTokenOptions: SearchableSelectType[] = useMemo(() => {
        return users
            ? users.map((user) => ({ label: user.name, value: user.token }))
            : [];
    }, [users]);

    const defaultLabel = testMessageInfo
        ? testMessageInfo.label.toLowerCase()
        : 'token';
    const testMessageTypeLabel = getTestMessageIdFieldLabel(
        providerName,
        formulaType,
        defaultLabel,
    );
    const menuPortal = document.getElementById(menuPortalId ?? '');

    const disableTestEmailMessage = useMemo<{
        isDisable: boolean;
        message: string;
    }>(() => {
        return getDisableTestEmailMessage(formulaType!, formula!);
    }, [formula, formulaType]);

    return (
        <>
            {!isModalView && (
                <>
                    {' '}
                    <Heading>Send a test message </Heading>
                    <Paragraph>
                        Aampe will randomly select one of your message
                        alternates and send it to the contact of your choice
                    </Paragraph>
                </>
            )}
            <StyledForm isModalView={isModalView}>
                <SavedTokenContainer isModalView={isModalView}>
                    <SavedTokenText isModalView={isModalView}>
                        If you have previously saved your {testMessageTypeLabel}{' '}
                        with a name you can select it from the dropdown
                    </SavedTokenText>

                    <AampeCreateSelectDropdown
                        label={isModalView ? 'User name' : ''}
                        options={notificationTokenOptions}
                        value={values.notification_token}
                        name={'notification_token'}
                        onChange={(
                            _,
                            value:
                                | SearchableSelectType
                                | SearchableSelectType[],
                        ) => {
                            setFieldValue('notification_token', value);
                            setFieldValue('contact_ids', [
                                (value as SearchableSelectType).value,
                            ]);
                        }}
                        placeholder={'Select User'}
                        onBlur={handleBlur}
                        isDisabled={notificationTokenOptions.length === 0}
                        wrapperStyle={{
                            width: isModalView ? '100%' : '50%',
                        }}
                        menuPortalTarget={isModalView ? menuPortal : undefined}
                    />
                </SavedTokenContainer>
                <InputContainer>
                    {isModalView && (
                        <TokenLabelRow>
                            <span>
                                {getCapitalizedText(testMessageTypeLabel)}
                            </span>
                            {handleNeedHelp &&
                                testMessageInfo?.type ===
                                    TestMessageType.Token && (
                                    <HelpLink onClick={handleNeedHelp}>
                                        Request help finding token
                                    </HelpLink>
                                )}
                        </TokenLabelRow>
                    )}
                    <TextInput
                        label=""
                        name="contact_id"
                        type="text"
                        placeholder={`Type in your ${testMessageTypeLabel}`}
                        value={values.contact_ids[0]}
                        onChange={(e) =>
                            setFieldValue('contact_ids[0]', e.target.value)
                        }
                        onBlur={handleBlur}
                        error={
                            'contact_ids' in errors
                                ? errors?.contact_ids?.[0]
                                : undefined
                        }
                        touched={
                            'contact_ids' in touched
                                ? touched?.contact_ids
                                : undefined
                        }
                        inputStyle={{
                            width: '100%',
                            paddingRight: aampeTheme.spacing(1),
                        }}
                        wrapperStyle={{ width: '100%' }}
                    />
                    {!isModalView && !isModalView && (
                        <AampeTooltip
                            title={
                                disableTestEmailMessage.isDisable
                                    ? disableTestEmailMessage.message
                                    : ''
                            }
                        >
                            <div>
                                <BasicButton
                                    type="submit"
                                    name="Send"
                                    isLoading={isLoading}
                                    disabled={disableTestEmailMessage.isDisable}
                                />
                            </div>
                        </AampeTooltip>
                    )}
                </InputContainer>
            </StyledForm>
        </>
    );
};

const Heading = styled.span(() => ({
    fontWeight: 'bold',
    fontSize: 16,
}));

const Paragraph = styled.p(() => ({
    fontSize: '14px',
    marginTop: aampeTheme.spacing(2),
    textAlign: 'center',
}));

const StyledForm = styled(Form).withConfig({
    shouldForwardProp: shouldForwardProps,
})<{ isModalView: boolean }>(({ isModalView }) => ({
    width: '100%',
    marginTop: isModalView ? '' : aampeTheme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: isModalView ? aampeTheme.spacing4(6) : aampeTheme.spacing(2),
}));

const InputContainer = styled.div(() => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
}));

const TokenLabelRow = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: aampeTheme.size.font.default,
    lineHeight: aampeTheme.size.lineHeight.small,
    fontWeight: aampeTheme.weights.medium,
    color: aampeTheme.colors.neutral09,
});

const HelpLink = styled.span({
    fontSize: aampeTheme.size.font.default,
    color: aampeTheme.colors.solidA01,
    cursor: 'pointer',
    fontWeight: aampeTheme.weights.medium,
});

const SavedTokenContainer = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<{ isModalView: boolean }>(({ isModalView }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: isModalView ? aampeTheme.spacing4(4) : '',
}));

const SavedTokenText = styled.p.withConfig({
    shouldForwardProp: shouldForwardProps,
})<{ isModalView: boolean }>(({ isModalView }) => ({
    fontSize: isModalView ? aampeTheme.size.font.default : '14px',
    margin: isModalView ? '' : aampeTheme.spacing(2, 0),
    fontWeight: isModalView ? 400 : '',
    lineHeight: isModalView ? '24px' : '',
    color: isModalView ? aampeTheme.colors.neutral09 : '',
}));

export default TestFormulaOneUserForm;
